import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';

interface NavbarProps {
  onNavigate: (page: string) => void;
  currentPage: string;
}

const Navbar: React.FC<NavbarProps> = ({ onNavigate, currentPage }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { cart } = useCart();
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const NavLink = ({ to, labelKey }: { to: string, labelKey: string }) => (
    <button
      onClick={() => { onNavigate(to); setMobileMenuOpen(false); }}
      className={`px-3 py-2 text-sm font-bold transition-colors ${
        currentPage === to ? 'text-blue-600' : 'text-slate-500 hover:text-blue-500'
      }`}
    >
      {t(labelKey)}
    </button>
  );

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <div className="flex items-center">
            <button 
              onClick={() => onNavigate('home')} 
              className="text-2xl font-black text-slate-900 flex items-center space-x-3 group"
            >
              <div className="w-12 h-12 bg-blue-50 rounded-2xl overflow-hidden flex items-center justify-center border border-blue-100 shadow-sm group-hover:rotate-6 transition-transform">
                <img 
                  src="https://images.unsplash.com/photo-1533738363-b7f9aef128ce?auto=format&fit=crop&w=80&q=80" 
                  alt="Piggy Dental Logo" 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="hidden sm:inline tracking-tighter uppercase">Piggy Dental</span>
            </button>
            <div className="hidden md:flex ml-10 space-x-4">
              <NavLink to="home" labelKey="home" />
              <NavLink to="catalog-lab" labelKey="lab" />
              <NavLink to="catalog-clinic" labelKey="clinic" />
              <NavLink to="printing-services" labelKey="printing" />
              {isAuthenticated && user?.role !== 'ADMIN' && <NavLink to="orders" labelKey="myOrders" />}
              {isAuthenticated && user?.role !== 'ADMIN' && <NavLink to="scans" labelKey="scans" />}
              {user?.role === 'ADMIN' && <NavLink to="admin" labelKey="admin" />}
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-6">
            <div className="hidden lg:flex items-center bg-slate-100 rounded-xl p-1 space-x-1">
              {(['en', 'zh', 'fr'] as const).map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${
                    language === lang ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>

            <button
              onClick={() => onNavigate('contact')}
              className={`hidden sm:block px-3 py-2 text-sm font-bold transition-colors ${
                currentPage === 'contact' ? 'text-blue-600' : 'text-slate-500 hover:text-blue-500'
              }`}
            >
              {t('contact')}
            </button>

            <div className="flex items-center space-x-2 sm:space-x-4">
                <button
                  onClick={() => onNavigate(isAuthenticated ? 'profile' : 'login')}
                  className={`p-2.5 rounded-2xl transition-all shadow-sm group ${
                    currentPage === 'profile' ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-900 hover:bg-blue-600 hover:text-white'
                  }`}
                  title={isAuthenticated ? "Profile Settings" : "Sign In"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>

                <button
                  onClick={() => onNavigate('cart')}
                  className={`relative p-2.5 rounded-2xl transition-all shadow-sm ${
                    currentPage === 'cart' ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-900 hover:bg-blue-600 hover:text-white'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full ring-4 ring-white">
                      {cartCount}
                    </span>
                  )}
                </button>

                {isAuthenticated ? (
                  <button
                    onClick={() => { logout(); onNavigate('home'); }}
                    className="hidden sm:block text-xs font-black text-slate-400 hover:text-red-500 transition-colors uppercase tracking-widest"
                  >
                    {t('logout')}
                  </button>
                ) : (
                  <button
                    onClick={() => onNavigate('login')}
                    className="hidden sm:block bg-blue-600 text-white px-6 py-3 rounded-2xl text-sm font-black hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 active:scale-95"
                  >
                    {t('signIn')}
                  </button>
                )}
            </div>
            
            <button 
              className="md:hidden p-2 text-slate-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-100 px-4 py-6 space-y-4 animate-fade-in">
          <NavLink to="home" labelKey="home" />
          <NavLink to="catalog-lab" labelKey="lab" />
          <NavLink to="catalog-clinic" labelKey="clinic" />
          <NavLink to="printing-services" labelKey="printing" />
          {isAuthenticated && <NavLink to="orders" labelKey="myOrders" />}
          {isAuthenticated && user?.role !== 'ADMIN' && <NavLink to="scans" labelKey="scans" />}
          {isAuthenticated && <NavLink to="profile" labelKey="profile" />}
          <NavLink to="contact" labelKey="contact" />
          <div className="flex space-x-2 pt-2 border-t border-slate-50">
            {(['en', 'zh', 'fr'] as const).map((lang) => (
              <button
                key={lang}
                onClick={() => { setLanguage(lang); setMobileMenuOpen(false); }}
                className={`flex-1 py-2 rounded-xl text-xs font-black uppercase border ${
                  language === lang ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-50 text-slate-400 border-slate-100'
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
